import { TabLines } from '@toggle/design-system';
import { AssetSubClassCode, Entity, SnakeMeta } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useCompanyTheme } from '~/hooks/use-knowledge-graph/useKnowledgeGraph';
import { TabsType } from '~/modules/tab-content/types';

import { EntityHeader } from '../entity-header/EntityHeader';
import { ChartPrice } from '../simple-chart/components/chart-price/ChartPrice';
import { getTabs } from '../tab-content/utils';
import * as S from './AssetOverviewHeader.styles';

export interface AssetOverviewHeaderProps {
  snakeMeta?: SnakeMeta;
  entity: Entity;
  activeTab: TabsType;
  changeTab: (index: number, tabKey: TabsType) => void;
  isCondensed: boolean;
  showShadow: boolean;
  documentsCount?: number;
}

export const AssetOverviewHeader = ({
  snakeMeta,
  entity,
  activeTab,
  changeTab,
  isCondensed,
  showShadow,
  documentsCount = 0,
}: AssetOverviewHeaderProps) => {
  const { t } = useTranslation([
    'chart',
    'analyze',
    'countries',
    'asset-overview',
  ]);

  const { error } = useCompanyTheme({ entityTag: entity.tag });

  const is404Error = error?.status === 404;
  const isStock = entity.sub_class === AssetSubClassCode.EquityStock;
  const tabs = getTabs(t, { isStock, documentsCount, is404Error });
  const filteredTabs = tabs.filter(tab => !tab.isHidden);

  return (
    <S.Header $showShadow={showShadow}>
      <S.Layout>
        <S.HeaderContent $hasEntity={!!entity} $isCondensed={isCondensed}>
          {entity && (
            <>
              <S.ContentWrapper $isCollapsed={isCondensed}>
                <EntityHeader
                  entity={entity}
                  priceDisplayNode={
                    <ChartPrice
                      entity={entity}
                      t={t}
                      snakeMeta={snakeMeta}
                      isCondensed={isCondensed}
                      enablePriceHover={false}
                    />
                  }
                  isCondensed={isCondensed}
                />
              </S.ContentWrapper>
              <TabLines
                tabs={filteredTabs}
                activeTab={activeTab}
                onChange={changeTab}
              />
            </>
          )}
        </S.HeaderContent>
      </S.Layout>
    </S.Header>
  );
};
